import React, { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

// AUTO-WA-39: messages per day. Two series on one axis; colours validated with
// the dataviz palette checker on white (blue #147DB5 / mint #2FA984; mint is
// under 3:1, so the legend and tooltip always name each series).
export const SERIES = [
  { key: 'outgoing', label: 'Sent by you', color: '#147DB5' },
  { key: 'incoming', label: 'Received', color: '#2FA984' },
];

const dayLabel = (value) => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="wa-tooltip">
      <strong>{dayLabel(label)}</strong>
      {SERIES.map((series) => {
        const item = payload.find((entry) => entry.dataKey === series.key);
        return (
          <span key={series.key}><i style={{ background: series.color }} aria-hidden="true" />{series.label}<b className="ws-num">{Number(item?.value || 0).toLocaleString('en-IN')}</b></span>
        );
      })}
    </div>
  );
}

export default function MessagesChart({ points = [] }) {
  const data = useMemo(() => points.map((point) => ({ day: point.day, incoming: Number(point.incoming) || 0, outgoing: Number(point.outgoing) || 0 })), [points]);
  const totals = useMemo(() => data.reduce((sum, point) => ({ incoming: sum.incoming + point.incoming, outgoing: sum.outgoing + point.outgoing }), { incoming: 0, outgoing: 0 }), [data]);

  return (
    <div className="wa-chart">
      <div className="wa-legend" role="list" aria-label="Series">
        {SERIES.map((series) => (
          <span key={series.key} role="listitem"><i style={{ background: series.color }} aria-hidden="true" />{series.label}<b className="ws-num">{totals[series.key].toLocaleString('en-IN')}</b></span>
        ))}
      </div>
      {data.length === 0 ? (
        <div className="ws-empty" style={{ height: 220 }}><strong>No messages in this period</strong>Pick a longer period to see activity.</div>
      ) : (
        <div className="wa-chart-plot" role="img" aria-label={`Messages per day: ${totals.outgoing} sent by you, ${totals.incoming} received.`}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
              <defs>
                {SERIES.map((series) => (
                  <linearGradient key={series.key} id={`wa-fill-${series.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={series.color} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={series.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid vertical={false} stroke="rgba(16,47,69,.07)" />
              <XAxis dataKey="day" tickFormatter={dayLabel} tick={{ fontSize: 11.5, fill: '#5B7180' }} axisLine={false} tickLine={false} minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11.5, fill: '#5B7180' }} axisLine={false} tickLine={false} width={44} />
              <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(16,47,69,.25)', strokeWidth: 1 }} />
              {SERIES.map((series) => (
                <Area key={series.key} type="monotone" dataKey={series.key} name={series.label} stroke={series.color} strokeWidth={2} fill={`url(#wa-fill-${series.key})`}
                  dot={false} activeDot={{ r: 4.5, stroke: '#fff', strokeWidth: 2, fill: series.color }} isAnimationActive={false} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
